import React, { useEffect, useState } from "react";
import axios from "axios";
import { Globe } from "lucide-react";
import { getClientAuthHeaders } from "@/lib/apiAuth";
import { sellingCountryOptions } from "@/constants/billingCatalog";

const BillingCountrySettings = () => {
  const [loaded, setLoaded] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [options, setOptions] = useState(sellingCountryOptions());

  const url = `${process.env.REACT_APP_BACKEND_URL}/api/account/billing-country`;
  const saveDisabled = loaded == null || draft === loaded || saving || loading;

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(url, { headers: getClientAuthHeaders() });
        setLoaded(data.billing_country);
        setDraft(data.billing_country);
        if (Array.isArray(data.options) && data.options.length === 5) {
          setOptions(data.options);
        }
      } catch (_err) {
        // loaded stays null; do not redirect to /login
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [url]);

  const handleSave = async () => {
    setSaving(true);
    setSuccess(false);
    setError("");
    try {
      const { data } = await axios.patch(
        url,
        { billing_country: draft },
        { headers: getClientAuthHeaders() }
      );
      setLoaded(data.billing_country);
      setDraft(data.billing_country);
      setSuccess(true);
      setError("");
    } catch (err) {
      setSuccess(false);
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === "string" ? detail : "Failed to update billing country"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="glass rounded-2xl p-8 md:p-10 border border-white/10 mb-6"
      data-testid="billing-country-card"
    >
      <div className="mb-8">
        <h2 className="font-heading font-bold text-lg md:text-xl text-[#F3F4F6] mb-2 flex items-center gap-2">
          <Globe className="w-5 h-5 text-[#00D1FF]" />
          Billing country
        </h2>
        <p className="text-[#9CA3AF] font-body text-sm">
          This only changes which regional prices you see. It does not change your current subscription, billing amount, or renewal date.
        </p>
      </div>

      {success && (
        <div className="mb-6 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
          <p className="text-green-400 text-sm font-body text-center">
            Billing country updated.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <p className="text-red-400 text-sm font-body text-center">{error}</p>
        </div>
      )}

      <div className="space-y-5">
        <select
          value={draft}
          disabled={loaded == null}
          onChange={(e) => {
            setDraft(e.target.value);
            setSuccess(false);
            setError("");
          }}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#F3F4F6] focus:outline-none focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50 font-body disabled:opacity-50"
          data-testid="billing-country-select"
        >
          {options.map((row) => (
            <option key={row.code} value={row.code}>
              {row.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          disabled={saveDisabled}
          onClick={handleSave}
          className={`w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 mt-8 ${
            !saveDisabled
              ? "btn-pulse bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
              : "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
          }`}
          data-testid="billing-country-save"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default BillingCountrySettings;
